import React, { useState } from 'react';
import { CiBarcode, CiCircleInfo } from 'react-icons/ci';
import { GrStatusGoodSmall } from 'react-icons/gr';
import { IoCopyOutline, IoPricetagsOutline } from 'react-icons/io5';
import { PiUsersThin } from 'react-icons/pi';
import { RiShareBoxLine } from 'react-icons/ri';
import { TbBuildingCommunity, TbClockCancel } from 'react-icons/tb';
import { IoIosTimer } from 'react-icons/io';

const CurrentPlanSection = ({ 
  isPlanOpen, 
  handleOpenCurrentPlan, 
  plan, 
  subscription, 
  name, 
  handleGoto, 
  setDeactivateModal,
  convertDate,
  calculateNextBillingDate
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(subscription.sub_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="w-full mx-auto mt-4 relative">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <button
          onClick={handleOpenCurrentPlan}
          className="w-full px-4 py-3 flex justify-between items-center focus:outline-none"
        >
          <span className="font-medium">Current plan</span>
          <svg
            className={`w-5 h-5 transition-transform duration-200 ${isPlanOpen ? 'transform rotate-180' : ''}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <div
          className={`transition-all duration-300 ease-in-out ${isPlanOpen ? 'max-h-fit opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}
        >
          <div className="px-4 py-3 border-t border-gray-200 flex flex-col gap-3">
            <span className='flex flex-col sm:flex-row justify-between sm:items-center gap-2'>
              <p className="text-sm font-semibold font-lufga text-gray-600">{plan.name}</p>
              <button
                onClick={handleGoto}
                className='flex items-center w-fit gap-0.5 text-blue-500 bg-blue-200 px-2 py-1 rounded-md shadow-md border hover:border-blue-600'>
                <RiShareBoxLine />
                <p className="text-xs font-semibold font-lufga">Go to {plan?.app}</p>
              </button>
            </span>
            <span className='flex flex-col sm:flex-row sm:items-center gap-2'>
              <span className='flex items-center gap-2'>
                <CiBarcode size={24} />
                <p className="text-sm text-gray-600">Subscription code: <span className='font-semibold bg-blue-50 rounded-lg px-2 py-1'>{subscription.sub_code}</span></p>
              </span>
              <span className='flex items-center gap-1'>
                <CiCircleInfo
                  className='cursor-pointer hidden lg:block'
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                />
                {showTooltip && (
                  <div className="absolute top-5 sm:top-12 left-[40%] transform -translate-x-1/2 bg-black text-white text-xs rounded py-1 px-2 mb-2 z-10">
                    When setting up your {plan?.app} account, please make sure to copy your subscription code and provide it where necessary. This code is essential for accessing your subscription benefits and ensuring a seamless experience.
                  </div>
                )}
                <IoCopyOutline
                  className='cursor-pointer hover:text-blue-500'
                  onClick={handleCopy}
                />
                {copied && (
                  <span className="ml-2 text-green-500 text-xs">Copied!</span>
                )}
              </span>
            </span>
            <span className='flex items-center gap-2'>
              <IoPricetagsOutline size={22} />
              <div>
                <div className="flex items-baseline">
                  <span className="text-base font-semibold text-blue-600">${plan.price}</span>
                  <span className="text-sm text-gray-600 ml-1">/ {plan.billable}</span>
                </div>
                {plan?.discounted_amount > 0 && (
                  <p className="text-sm text-gray-500 line-through">${plan.discounted_amount} / {plan.billable}</p>
                )}
              </div>
            </span>
            <span className='flex items-center gap-2'>
              <TbBuildingCommunity size={24} />
              <p className="text-sm text-gray-600">Branch coverage: Up to <span className='font-bold'>{plan?.branches_limit}</span> branches</p>
            </span>
            <span className='flex items-center gap-2'>
              <PiUsersThin size={24} />
              <p className="text-sm text-gray-600">User coverage: Up to <span className='font-bold'>{plan?.users_limit}</span> users</p>
            </span>
            <span className='flex items-center gap-2'>
              <IoIosTimer size={24} />
              <div className="text-sm text-gray-600">
                Next billing date: {" "}
                {subscription?.stripe_status === 'active' ? (
                  (() => {
                    if (subscription?.expires_on) {
                      return subscription.expires_on.split(' ')[0]; // Just return the date part
                    } 
                    else if (subscription?.created_at) {
                      return calculateNextBillingDate(subscription.created_at, plan.billable);
                    } 
                    else {
                      return "Date information unavailable";
                    }
                  })()
                ) : (
                  <span className="text-yellow-600">Subscription not active</span>
                )}
              </div>
            </span>

            <span className='flex flex-col sm:flex-row justify-between sm:items-center gap-2 sm:gap-0'>
              <span className='flex items-center gap-2'>
                <GrStatusGoodSmall size={20} className={`${subscription.stripe_status === 'active' ? 'text-green-600 animate-pulse' : 'text-red-600'}`} />
                <p className="text-sm text-gray-600">Status: {subscription.stripe_status}</p>
              </span>
              <button
                onClick={() => { setDeactivateModal(true) }}
                className='flex w-fit items-center gap-0.5 text-red-500 bg-red-200 px-2 py-1 rounded-md shadow-md border hover:border-red-600'>
                <TbClockCancel size={20} />
                <p className="text-xs font-semibold font-lufga">Deactivate</p>
              </button>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentPlanSection; 